import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { memberRoleSchema } from '../workspace/workspace.js';
export const FOUNDER_STATUSES = ['ACTIVE', 'INACTIVE', 'DEPARTED'];
export const founderStatusSchema = z.enum(FOUNDER_STATUSES);
export const CAP_TABLE_STATUSES = ['DRAFT', 'APPROVED', 'CORRECTED', 'DISCARDED'];
export const capTableStatusSchema = z.enum(CAP_TABLE_STATUSES);
/** Percent units with at most 6 decimals, e.g. `"20"` or `"33.333334"` (storage `numeric(9,6)`). */
export const percentageInputSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,6})?$/, 'Use a percentage such as 20 or 33.333334 (max 6 decimals)');
export const shareCountInputSchema = z
    .string()
    .trim()
    .regex(/^[1-9]\d{0,17}$/, 'Use a whole number of shares');
export const legalNameSchema = z.string().trim().min(1).max(300);
export const contactPhoneSchema = z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{5,39}$/, 'Use digits, spaces, (), - and an optional leading +');
/** Equity position derived from an approved cap-table version (provenance always included). */
export const equityPositionSchema = z.object({
    percentage: z.string(),
    shareCount: z.string().nullable(),
    capTableVersionId: uuidSchema,
    versionNumber: z.number().int(),
    effectiveFrom: isoDateSchema,
    approvedAt: isoDateTimeSchema.nullable(),
    approvalDecisionId: uuidSchema.nullable(),
    approvalReference: z.string().nullable(),
});
export const founderSummarySchema = z.object({
    id: uuidSchema,
    memberId: uuidSchema,
    displayName: z.string(),
    title: z.string().nullable(),
    role: memberRoleSchema,
    status: founderStatusSchema,
    founderSince: isoDateSchema.nullable(),
    departedOn: isoDateSchema.nullable(),
    /** Present only for members allowed to read equity (`equity.read`). */
    legalName: z.string().nullable(),
    currentEquity: equityPositionSchema.nullable(),
});
export const founderListSchema = z.object({
    asOf: isoDateSchema,
    equityVisible: z.boolean(),
    items: z.array(founderSummarySchema),
});
export const equityHistoryItemSchema = z.object({
    capTableVersionId: uuidSchema,
    versionNumber: z.number().int(),
    status: capTableStatusSchema,
    effectiveFrom: isoDateSchema,
    /** `null` when the founder has no entry in this version (e.g. admitted later). */
    percentage: z.string().nullable(),
    shareCount: z.string().nullable(),
    reason: z.string(),
    approvedAt: isoDateTimeSchema.nullable(),
    approvalDecisionId: uuidSchema.nullable(),
    approvalReference: z.string().nullable(),
});
export const founderDetailSchema = founderSummarySchema.extend({
    departureReason: z.string().nullable(),
    /** Visible to the founder themself and to members who manage founders. */
    contactPhone: z.string().nullable(),
    equityHistory: z.array(equityHistoryItemSchema),
    version: versionSchema,
});
export const createFounderProfileRequestSchema = z.object({
    memberId: uuidSchema,
    legalName: legalNameSchema,
    founderSince: isoDateSchema.nullable().optional(),
});
export const updateFounderProfileRequestSchema = z
    .object({
    version: versionSchema,
    legalName: legalNameSchema.optional(),
    founderSince: isoDateSchema.nullable().optional(),
    contactPhone: contactPhoneSchema.nullable().optional(),
})
    .refine((value) => value.legalName !== undefined ||
    value.founderSince !== undefined ||
    value.contactPhone !== undefined, { message: 'Nothing to update' });
export const changeFounderStatusRequestSchema = z
    .object({
    version: versionSchema,
    status: founderStatusSchema,
    reason: reasonSchema,
    departedOn: isoDateSchema.optional(),
})
    .refine((value) => value.status !== 'DEPARTED' || value.departedOn !== undefined, {
    message: 'A departure date is required',
    path: ['departedOn'],
});
export const capTableEntrySchema = z.object({
    founderProfileId: uuidSchema,
    founderName: z.string(),
    percentage: z.string(),
    shareCount: z.string().nullable(),
    note: z.string().nullable(),
});
export const capTableVersionSchema = z.object({
    id: uuidSchema,
    versionNumber: z.number().int(),
    status: capTableStatusSchema,
    effectiveFrom: isoDateSchema,
    reason: z.string(),
    totalPercentage: z.string(),
    correctsVersionId: uuidSchema.nullable(),
    approvalDecisionId: uuidSchema.nullable(),
    approvalReference: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: isoDateTimeSchema.nullable(),
    createdBy: z.string(),
    createdAt: isoDateTimeSchema,
    discardedAt: isoDateTimeSchema.nullable(),
    correctedAt: isoDateTimeSchema.nullable(),
    entries: z.array(capTableEntrySchema),
    version: versionSchema,
});
export const capTableSchema = z.object({
    asOf: isoDateSchema,
    /** Approved version in force on `asOf` (greatest effective date ≤ asOf). */
    effectiveVersionId: uuidSchema.nullable(),
    versions: z.array(capTableVersionSchema),
});
export const capTableQuerySchema = z.object({ asOf: isoDateSchema.optional() });
export const createCapTableDraftRequestSchema = z.object({
    effectiveFrom: isoDateSchema,
    reason: reasonSchema,
    correctsVersionId: uuidSchema.nullable().optional(),
    entries: z
        .array(z.object({
        founderProfileId: uuidSchema,
        percentage: percentageInputSchema,
        shareCount: shareCountInputSchema.nullable().optional(),
        note: z.string().trim().max(500).nullable().optional(),
    }))
        .min(1)
        .max(100),
});
/** Approval records a founder decision; the decision must be FINALIZED (enforced by the database). */
export const approveCapTableVersionRequestSchema = z.object({
    version: versionSchema,
    decisionId: uuidSchema,
});
export const discardCapTableVersionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
//# sourceMappingURL=founders.js.map